import { Button, Grid } from '@material-ui/core'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { register } from '../../../actions/userActions'
import { USER_LOGOUT_RESET } from '../../../constants/userConstants'
import { useInput } from '../../../hooks/useInput'
import { usePasswordValidator } from '../../../hooks/usePasswordValidator'
import CheckboxInput from '../../Form/CheckboxInput'
import TextInput from '../../Form/TextInput'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import HelpTooltip from '../../Tooltip/HelpTooltip'
import './signUp.scss'

const SignUp = ({ location }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const {
    value: firstName,
    bind: bindFirstName,
    error: firstNameError,
    setError: setFirstNameError,
  } = useInput('')
  const {
    value: lastName,
    bind: bindLastName,
    error: lastNameError,
    setError: setLastNameError,
  } = useInput('')
  const {
    value: email,
    bind: bindEmail,
    error: emailError,
    setError: setEmailError,
  } = useInput('')
  const {
    value: password,
    bind: bindPassword,
    error: passwordError,
    setError: setPasswordError,
  } = useInput('')
  const { value: confirmPassword, bind: bindConfirmPassword } = useInput('')
  const [termsAndService, setTermsAndService] = useState(false)
  const [termsAndServiceError, setTermsAndServiceError] = useState('')
  const [marketingConsent, setMarketingConsent] = useState(false)
  const passwordIsValid = usePasswordValidator(password)
  const [formError, setFormError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { loading, error, success } = useSelector((state) => state.userRegister)
  const { userInfo, userLoggedOut } = useSelector((state) => state.userLogin)

  useEffect(() => {
    if (userInfo) {
      history.push('/myquality/dashboard')
    }

    if (success) {
      history.push(`/signup-success`)
    }

    if (userLoggedOut) {
      dispatch({
        type: USER_LOGOUT_RESET,
      })
      window.location.reload()
    }
  }, [userInfo, success, dispatch, history, userLoggedOut])

  const formIsValid = () => {
    let ready = true

    if (firstName === '') {
      setFirstNameError('Please enter your first name.')
      ready = false
    } else {
      setFirstNameError('')
    }

    if (lastName === '') {
      setLastNameError('Please enter your last name.')
      ready = false
    } else {
      setLastNameError('')
    }

    if (email === '') {
      setEmailError('Please enter a valid email.')
      ready = false
    } else if (email.toLowerCase().includes('@hexagon.com')) {
      setEmailError('Please request an invitation from your manager.')
      setFormError(
        'Hexagon employees must receive an invitiation to join. Please contact a manager.'
      )
      ready = false
    } else if (
      email.toLowerCase().includes('@gmail.com') ||
      email.toLowerCase().includes('@yahoo.com') ||
      email.toLowerCase().includes('@aol.com') ||
      email.toLowerCase().includes('@hotmail.com')
    ) {
      setEmailError('Please use your work email.')
      setFormError('Please enter your work email account to sign up.')
      ready = false
    } else {
      setFormError('')
      setEmailError('')
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match')
      ready = false
    } else {
      setPasswordError('')
    }

    if (passwordIsValid !== '') {
      ready = false
      setPasswordError(passwordIsValid)
    }

    if (!termsAndService) {
      setTermsAndServiceError(
        'You must agree to Terms & Conditions and Privacy Policy'
      )
      ready = false
    } else {
      setTermsAndServiceError('')
    }

    return ready
  }

  const submitHandler = (e) => {
    e.preventDefault()

    if (formIsValid()) {
      dispatch(
        register(
          firstName,
          lastName,
          email.toLowerCase(),
          password,
          marketingConsent
        )
      )
    }
  }

  return (
    <form onSubmit={submitHandler}>
      <div>
        {loading ? (
          <Loader />
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput
                  name="firstName"
                  label="First name"
                  bindValue={bindFirstName}
                  error={firstNameError}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  name="lastName"
                  label="Last name"
                  bindValue={bindLastName}
                  error={lastNameError}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  name="email"
                  type="email"
                  label="Work email"
                  bindValue={bindEmail}
                  error={emailError}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  label={
                    <>
                      Password
                      <HelpTooltip
                        title={
                          <div>
                            <p className="m-b-0">Password requirements: </p>
                            <ul className="p-l-30">
                              <li>At least 8 characters</li>
                              <li>Upper and lowercase letters</li>
                              <li>
                                Inclusion of at least one special character
                              </li>
                            </ul>
                          </div>
                        }
                      />
                    </>
                  }
                  bindValue={bindPassword}
                  error={passwordError}
                  endAdornment={true}
                  handleClick={() => setShowPassword(!showPassword)}
                  icon={showPassword ? <VisibilityOff /> : <Visibility />}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm password"
                  bindValue={bindConfirmPassword}
                  error={passwordError}
                  endAdornment={true}
                  handleClick={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  icon={
                    showConfirmPassword ? <VisibilityOff /> : <Visibility />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <CheckboxInput
                  name="termsAndService"
                  value={termsAndService}
                  setValue={setTermsAndService}
                  label={
                    <div className="fs14 m-t-5">
                      I agree to Hexagon MyQuality{' '}
                      <a
                        className="link"
                        href="https://www.hexagonmi.com/en-US/about-us/terms-of-use"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Terms of use
                      </a>{' '}
                      and{' '}
                      <a
                        className="link"
                        target="_blank"
                        href="https://www.hexagonmi.com/en-US/about-us/privacy-policy"
                        rel="noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </div>
                  }
                  error={termsAndServiceError}
                />
              </Grid>

              <Grid item xs={12}>
                <CheckboxInput
                  name="marketingConsent"
                  value={marketingConsent}
                  setValue={setMarketingConsent}
                  label={
                    <div className="fs14 m-t-5">
                      I consent to receive marketing communications on products,
                      services, publications, and events.
                    </div>
                  }
                />
              </Grid>
            </Grid>
            <div className="flex flex-end">
              <Button
                variant="contained"
                className="m-t-30"
                color="primary"
                type="submit"
              >
                Sign up for free
              </Button>
            </div>

            {!loading && termsAndServiceError && (
              <Message variant="error">{termsAndServiceError}</Message>
            )}

            {!loading && error && <Message variant="error">{error}</Message>}

            {!loading && formError && (
              <Message variant="error">{formError}</Message>
            )}
          </>
        )}
      </div>
    </form>
  )
}

export default SignUp
